import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { isSmoothScrolling } from './smooth-scroll';

gsap.registerPlugin(ScrollTrigger);

// Shared defaults used across all scroll-triggered animations
const EASE = 'power2.out';
const DURATION = 0.9;

// Bypass vanilla-lazyload for images that are about to be clipped by GSAP.
// clip-path on a parent prevents IntersectionObserver from detecting child images
// as visible, so lazyload never fires for them. We manually set src here,
// before the clip-path is applied, so the browser starts downloading the image.
function forceLazyLoad(img) {
  if (img.classList.contains('lazyloaded')) return;
  const src = img.getAttribute('data-lazy-src');
  const srcset = img.getAttribute('data-lazy-srcset');
  const sizes = img.getAttribute('data-lazy-sizes');
  if (!src) return;
  // Mark the image as loaded ourselves: lazyload's observer does not see it
  // while it is clipped, so its fade-in would only start after the reveal.
  const onLoad = () => {
    img.removeEventListener('load', onLoad);
    img.classList.add('lazyloaded');
  };
  img.addEventListener('load', onLoad);
  if (sizes) img.sizes = sizes;
  if (srcset) img.srcset = srcset;
  img.src = src;
}

// Generic helper — kept for potential reuse but not called directly at the moment
function fromScroll(targets, vars, triggerEl) {
  if (!document.querySelector(targets) && !triggerEl) return;
  return gsap.from(targets, {
    scrollTrigger: {
      trigger: triggerEl || targets,
      start: 'top 82%',
      once: true,
    },
    ...vars,
  });
}

// Reveal the hero image on page load with a cinematic clip-path sweep + zoom-out.
// Text elements stagger in after the image has started opening.
// The hero image is loaded eagerly (loading="eager" skips the lazyload buffer), but a
// large file can still be on its way: the sweep only starts once the image has loaded.
// If that takes longer than HERO_WAIT ms, or the image fails, the sweep is skipped and
// the image simply shows as it arrives, so the hero never opens onto an empty frame.
const HERO_WAIT = 1200;

function heroEntrance() {
  const hero = document.querySelector('.s-hero');
  if (!hero) return;

  const content = hero.querySelector('.s-hero__content');
  const elements = content
    ? [...content.children].filter(
        (el) => el.innerText?.trim().length > 0 || el.querySelector('img, svg')
      )
    : [];

  if (elements.length) gsap.set(elements, { opacity: 0, y: 24 });

  const showText = (delay) => {
    if (!elements.length) return;
    gsap.to(elements, {
      opacity: 1,
      y: 0,
      duration: DURATION,
      stagger: 0.15,
      ease: EASE,
      delay,
      clearProps: 'all',
    });
  };

  const heroImage = hero.querySelector('.s-hero__image');
  if (!heroImage) {
    showText(0.6);
    return;
  }

  gsap.set(heroImage, { clipPath: 'inset(100% 0 0 0)', scale: 1.1 });

  let settled = false;
  const sweep = () => {
    if (settled) return;
    settled = true;
    gsap.to(heroImage, {
      clipPath: 'inset(0% 0 0 0)',
      scale: 1,
      duration: 1.6,
      ease: 'power3.inOut',
      clearProps: 'all',
    });
    showText(0.6);
  };
  const skip = () => {
    if (settled) return;
    settled = true;
    gsap.set(heroImage, { clearProps: 'all' });
    showText(0);
  };

  // A complete image without a width has already failed; its error event is gone.
  if (heroImage.complete) {
    if (heroImage.naturalWidth) {
      sweep();
    } else {
      skip();
    }
  } else {
    heroImage.addEventListener('load', sweep, { once: true });
    heroImage.addEventListener('error', skip, { once: true });
    setTimeout(skip, HERO_WAIT);
  }
}

// Move the hero image down slightly slower than the page scrolls. Its wrapper is taller
// than the hero (see _hero.scss), so it can shift by the difference without showing an
// edge. The wrapper moves, not the image, so the entrance tween above keeps the image.
function heroParallax() {
  const hero = document.querySelector('.s-hero');
  const media = hero && hero.querySelector('.s-hero__media');
  if (!media || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  gsap.to(media, {
    y: () => media.offsetHeight - hero.offsetHeight,
    ease: 'none',
    scrollTrigger: {
      trigger: hero,
      start: 0,
      end: 'bottom top',
      scrub: true,
      invalidateOnRefresh: true,
    },
  });
}

// Fade heading sections (title, text, image) in as they enter the viewport.
function headingEntrance() {
  document.querySelectorAll('.s-heading').forEach((section) => {
    const els = section.querySelectorAll('.s-heading__title, .s-heading__text, .s-heading__image');
    if (!els.length) return;
    gsap.from(els, {
      scrollTrigger: { trigger: section, start: 'top 82%', once: true },
      opacity: 0,
      y: 28,
      duration: DURATION,
      stagger: 0.15,
      ease: EASE,
      clearProps: 'all',
    });
  });
}

// Stagger-fade each USP item upward as the section scrolls into view.
function uspsEntrance() {
  const section = document.querySelector('.s-usps');
  if (!section) return;

  gsap.from(section.querySelectorAll('.s-usps__item'), {
    scrollTrigger: { trigger: section, start: 'top 80%', once: true },
    opacity: 0,
    y: 36,
    duration: DURATION,
    stagger: 0.12,
    ease: EASE,
    clearProps: 'all',
  });
}

// Animate the projects section header and project tiles on scroll.
// Images reveal via clip-path (bottom-to-top sweep) while simultaneously zooming out
// from scale 1.15 — a cinematic "curtain reveal" effect. Text fades in with a delay.
function projectsEntrance() {
  document.querySelectorAll('.s-projects').forEach((section) => {
    const head = section.querySelectorAll('.s-projects__title, .s-projects__link');
    const images = section.querySelectorAll('.project-item__image');
    const mains = section.querySelectorAll('.project-item__main');

    if (head.length) {
      gsap.from(head, {
        scrollTrigger: { trigger: section, start: 'top 82%', once: true },
        opacity: 0,
        y: 24,
        duration: DURATION,
        stagger: 0.1,
        ease: EASE,
        clearProps: 'all',
      });
    }

    // Each image and text block gets its own trigger so images below the fold
    // reveal individually as the user scrolls to them, not all at once.
    images.forEach((img) => {
      const item = img.closest('.project-item') || img;

      ScrollTrigger.create({
        trigger: item,
        // Start the download well before the tile scrolls into view.
        start: 'top bottom+=400',
        once: true,
        onEnter: () => {
          forceLazyLoad(img);
          gsap.set(img, { clipPath: 'inset(100% 0 0 0)', scale: 1.15 });
        },
      });

      ScrollTrigger.create({
        trigger: item,
        start: 'top 88%',
        once: true,
        onEnter: () => {
          gsap.to(img, {
            clipPath: 'inset(0% 0 0 0)',
            duration: 1.1,
            ease: 'power3.inOut',
            clearProps: 'all',
          });
          gsap.to(img, {
            scale: 1,
            duration: 1.1,
            ease: 'power3.out',
            clearProps: 'all',
          });
        },
      });
    });

    mains.forEach((main) => {
      gsap.from(main, {
        scrollTrigger: { trigger: main, start: 'top 92%', once: true },
        opacity: 0,
        y: 16,
        duration: DURATION,
        delay: 0.4,
        ease: EASE,
        clearProps: 'all',
      });
    });
  });
}

// Animate the products section header and individual product items on scroll.
// Items slide in from the left to reinforce a list/catalogue feel.
function productsEntrance() {
  document.querySelectorAll('.s-products').forEach((section) => {
    const head = section.querySelectorAll('.s-products__title, .s-products__link');
    const items = section.querySelectorAll('.s-products__item');

    if (head.length) {
      gsap.from(head, {
        scrollTrigger: { trigger: section, start: 'top 82%', once: true },
        opacity: 0,
        y: 24,
        duration: DURATION,
        stagger: 0.1,
        ease: EASE,
        clearProps: 'all',
      });
    }

    if (items.length) {
      gsap.from(items, {
        scrollTrigger: { trigger: section, start: 'top 80%', once: true },
        opacity: 0,
        x: -20,
        duration: 0.7,
        stagger: 0.08,
        ease: EASE,
        clearProps: 'all',
      });
    }
  });
}

// Animate side-by-side sections: image reveals with a clip-path sweep + scale,
// editor content fades up alongside. The image_wrap already has overflow:hidden
// which clips the overscaled image during the reveal.
function sbsEntrance() {
  document.querySelectorAll('.s-sbs').forEach((section) => {
    const image = section.querySelector('.s-sbs__image');
    const editor = section.querySelector('.s-sbs__editor');

    if (image) {
      ScrollTrigger.create({
        trigger: section,
        start: 'top bottom+=400',
        once: true,
        onEnter: () => {
          forceLazyLoad(image);
          gsap.set(image, { clipPath: 'inset(100% 0 0 0)', scale: 1.12 });
        },
      });

      ScrollTrigger.create({
        trigger: section,
        start: 'top 80%',
        once: true,
        onEnter: () => {
          gsap.to(image, {
            clipPath: 'inset(0% 0 0 0)',
            duration: 1.5,
            ease: 'power3.inOut',
            clearProps: 'all',
          });
          gsap.to(image, {
            scale: 1,
            duration: 1.5,
            ease: 'power3.out',
            clearProps: 'all',
          });
        },
      });
    }

    if (editor) {
      gsap.from(editor, {
        scrollTrigger: { trigger: section, start: 'top 80%', once: true },
        opacity: 0,
        y: 32,
        duration: 1,
        delay: 0.2,
        ease: EASE,
        clearProps: 'all',
      });
    }
  });
}

// Stagger-fade CTA content elements (pre-title, title, text, action) upward on scroll.
function ctaEntrance() {
  document.querySelectorAll('.s-cta').forEach((section) => {
    const els = section.querySelectorAll(
      '.s-cta__pre_title, .s-cta__title, .s-cta__text, .s-cta__action'
    );
    if (!els.length) return;
    gsap.from(els, {
      scrollTrigger: { trigger: section, start: 'top 78%', once: true },
      opacity: 0,
      y: 28,
      duration: DURATION,
      stagger: 0.15,
      ease: EASE,
      clearProps: 'all',
    });
  });
}

// Fade editor (WYSIWYG content) sections in as they scroll into view.
function editorEntrance() {
  document.querySelectorAll('.s-editor').forEach((section) => {
    gsap.from(section, {
      scrollTrigger: { trigger: section, start: 'top 82%', once: true },
      opacity: 0,
      y: 24,
      duration: DURATION,
      ease: EASE,
      clearProps: 'all',
    });
  });
}

// Attach GSAP-driven image zoom to every project tile.
// Using GSAP instead of a CSS transition guarantees a smooth, frame-perfect scale —
// CSS transitions on absolutely-positioned images can snap on some browsers/GPUs.
// overwrite: 'auto' cancels only conflicting scale tweens, leaving any in-progress
// clip-path reveal animation untouched (overwrite: true would kill all tweens).
function projectItemHovers() {
  document.querySelectorAll('.project-item').forEach((item) => {
    const image = item.querySelector('.project-item__image');
    if (!image) return;

    item.addEventListener('mouseenter', () => {
      gsap.to(image, {
        scale: 1.06,
        duration: 0.9,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    });

    item.addEventListener('mouseleave', () => {
      gsap.to(image, {
        scale: 1,
        duration: 0.7,
        ease: 'power2.inOut',
        overwrite: 'auto',
      });
    });
  });
}

// Slide the footer up into view from a masked window as the user scrolls to it.
// .footer has overflow:hidden acting as the mask; .footer__reveal starts shifted
// up by half its own height and animates back to its natural position, scrubbed
// to scroll position. No fixed height is used — the mask is just the footer's
// own natural content height, so this works regardless of ACF-driven footer length.
function footerReveal() {
  const footer = document.querySelector('.footer');
  const reveal = footer && footer.querySelector('.footer__reveal');
  if (!footer || !reveal) return;

  gsap.set(reveal, { yPercent: -50 });

  const uncover = gsap.timeline({ paused: true });
  uncover.to(reveal, { yPercent: 0, ease: 'none' });

  ScrollTrigger.create({
    trigger: footer,
    start: 'top bottom',
    end: 'bottom bottom',
    animation: uncover,
    scrub: true,
  });
}

// The timeline dot is position: sticky, which cannot stick inside the smooth-scroll
// wrapper: that wrapper never scrolls itself, only its content moves. While the
// smoother runs, the dot is moved down by the distance scrolled instead, over the
// same stretch the sticky would cover (from its `top` offset to the separator's end).
let timelineDotTriggers = [];

function timelineDot() {
  timelineDotTriggers.forEach((trigger) => trigger.kill());
  timelineDotTriggers = [];

  document.querySelectorAll('.s-timeline__dot').forEach((dot) => {
    gsap.set(dot, { clearProps: 'transform' });
  });
  if (!isSmoothScrolling()) return;

  document.querySelectorAll('.s-timeline__dot').forEach((dot) => {
    const separator = dot.parentElement;
    let baseY;
    const measureBaseY = () => {
      gsap.set(dot, { clearProps: 'transform' });
      baseY = gsap.getProperty(dot, 'y'); // translateY(-0.5rem) from the CSS
    };
    measureBaseY();

    timelineDotTriggers.push(
      ScrollTrigger.create({
        trigger: separator,
        start: () =>
          `top ${
            (parseFloat(getComputedStyle(dot).top) || 0) + baseY - dot.offsetTop
          }px`,
        end: () =>
          `+=${Math.max(
            0,
            separator.clientHeight - dot.offsetTop - dot.offsetHeight
          )}`,
        onRefreshInit: measureBaseY,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          gsap.set(dot, { y: baseY + self.progress * (self.end - self.start) });
        },
      })
    );
  });
}

// Initialise all animations for the page.
export function initAnimations() {
  heroEntrance();
  heroParallax();
  headingEntrance();
  uspsEntrance();
  projectsEntrance();
  productsEntrance();
  sbsEntrance();
  ctaEntrance();
  editorEntrance();
  projectItemHovers();
  footerReveal();
  timelineDot();
  document.addEventListener('smoothscrollchange', timelineDot);

  ScrollTrigger.refresh();
}
